import { useState } from 'react';
import { appScenarios, ScenarioState } from './data/mock-data';
import { UploadPanel } from './components/UploadPanel';
import { MapSurface } from './components/MapSurface';
import { DetailSheet } from './components/DetailSheet';
import { hasLiveConfig } from './lib/supabase';
import { AuthGate } from './components/AuthGate';
import { LiveApp } from './LiveApp';
import { DateRangeFilter, isPhotoInDateRange } from './lib/photoFilters';

export function MockApp() {
  const [scenario, setScenario] = useState<ScenarioState>('ready');
  const scenarioState = appScenarios[scenario];
  const [dateRange, setDateRange] = useState<DateRangeFilter>({ start: '', end: '' });
  
  // Keep local state for selection to allow user interaction,
  // falling back to scenario default if local state isn't in current scenario
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(scenarioState.selectedPhotoId);

  const filteredPhotos = scenarioState.photos.filter((photo) => isPhotoInDateRange(photo, dateRange));
  const isFilteredEmpty = Boolean(dateRange.start || dateRange.end)
    && scenarioState.photos.length > 0
    && filteredPhotos.length === 0;
  const filteredScenarioState = {
    ...scenarioState,
    photos: filteredPhotos,
    selectedPhotoId: selectedPhotoId,
  };
  const activePhotoId = filteredPhotos.some((p) => p.id === selectedPhotoId)
    ? selectedPhotoId
    : filteredPhotos[0]?.id ?? null;

  const handleScenarioChange = (newScenario: ScenarioState) => {
    setScenario(newScenario);
    setSelectedPhotoId(appScenarios[newScenario].selectedPhotoId);
  };

  const handleSelectPhoto = (id: string) => {
    setSelectedPhotoId(id);
    if (scenario !== 'ready') {
      setScenario('ready');
    }
  };

  return (
    <div className="app-shell">
      <div className="shell-frame">
        <header className="shell-header">
          <div className="shell-header__main">
            <p className="shell-header__eyebrow">굴착공사사진 확인</p>
            <h1 className="shell-header__title">굴착공사사진 확인</h1>
            <p className="shell-header__subtitle">
              서산·태안 굴착 현장 사진의 GPS와 촬영 시각으로 지중시설 기록을 지도에서 검토합니다.
            </p>
          </div>
          <UploadPanel
            scenarioState={filteredScenarioState}
            rootScenario={scenario}
            onScenarioChange={handleScenarioChange}
            onSelectPhoto={handleSelectPhoto}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onDateRangeReset={() => setDateRange({ start: '', end: '' })}
            isFilteredEmpty={isFilteredEmpty}
            mode="preview"
            showScenarioControls={false}
            variant="top"
          />
        </header>

        <div className="shell-layout">
          <MapSurface 
            scenarioState={filteredScenarioState} 
            onSelectPhoto={handleSelectPhoto} 
            selectedPhotoId={activePhotoId} 
            isFilteredEmpty={isFilteredEmpty}
          />
          <DetailSheet 
            scenarioState={filteredScenarioState} 
            onSelectPhoto={handleSelectPhoto} 
            selectedPhotoId={activePhotoId} 
            isFilteredEmpty={isFilteredEmpty}
          />
        </div>
      </div>
    </div>
  );
}

export default function App() {
  if (hasLiveConfig) {
    return (
      <AuthGate>
        <LiveApp />
      </AuthGate>
    );
  }

  return <MockApp />;
}

